import React, { useState } from 'react';
import {
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  Button,
  Tooltip,
  Paper,
  Grid,
  useTheme,
  alpha,
  FormLabel,
} from '@mui/material';
import { useStore } from 'zustand';
import { defaultGlobalItem, useGlobalItemStore } from '../../../stores/globalItemStore';
import { AttributeGroup } from '../../../api/protosCompiled/globalItem/globalItem_pb';
import { useAssemblyStore } from '../../../stores/AssemblyStore';
import { useFolderStore } from '../../../stores/folderStore';

export interface SelectedAttributesMap {
  [key: string]: string[];
}

interface AttributeValuesCheckboxesProps {
  selectedAttributeGroupId: string | null;
  selectedAttributes: SelectedAttributesMap;
  setSelectedAttributes: React.Dispatch<React.SetStateAction<SelectedAttributesMap>>;
  attributeGroups?: Array<AttributeGroup.AsObject>;
  handleCheckboxChange: (groupId: string, valueId: string) => void;
  itemName: string;
  setItemName: (name: string) => void;
  selectedUom: string;
  setSelectedUom: (uom: string) => void;
  handleCreateItem: () => void;
  userBackgroundColor?: string;
}

function naturalCompare(a: string, b: string): number {
  const chunkRegex = /(\d+|\D+)/g;

  const aChunks = a.match(chunkRegex) || [];
  const bChunks = b.match(chunkRegex) || [];

  const len = Math.max(aChunks.length, bChunks.length);

  for (let i = 0; i < len; i++) {
    const aChunk = aChunks[i] || '';
    const bChunk = bChunks[i] || '';

    const aNum = parseInt(aChunk, 10);
    const bNum = parseInt(bChunk, 10);

    const aIsNum = !isNaN(aNum);
    const bIsNum = !isNaN(bNum);

    if (aIsNum && bIsNum) {
      if (aNum !== bNum) return aNum - bNum;
    } else if (aIsNum && !bIsNum) {
      return -1;
    } else if (!aIsNum && bIsNum) {
      return 1;
    } else {
      const cmp = aChunk.localeCompare(bChunk);
      if (cmp !== 0) return cmp;
    }
  }

  return a.length - b.length;
}

const sortAndGroupAttributeValues = (
  attributeValues: AttributeGroup.AsObject['attributevaluesList']
) => {
  // First, group by attributevaluename to eliminate duplicates
  const uniqueValues = attributeValues.reduce(
    (acc, value) => {
      const key = value.attributevaluename;
      if (!acc[key] || acc[key].attributevalueid > value.attributevalueid) {
        // Keep the value with the lowest ID if there are duplicates
        acc[key] = value;
      }
      return acc;
    },
    {} as Record<string, AttributeGroup.AsObject['attributevaluesList'][0]>
  );

  // Convert back to array and sort
  const sortedValues = Object.values(uniqueValues).sort((a, b) =>
    naturalCompare(a.attributevaluename, b.attributevaluename)
  );

  // Group by first character or other criteria if needed
  const groupedValues: Record<string, AttributeGroup.AsObject['attributevaluesList']> = {};

  for (const value of sortedValues) {
    const name = value.attributevaluename;
    const firstChar = name.charAt(0);
    const groupKey = /^\d/.test(firstChar) ? 'Numeric' : firstChar.toUpperCase();

    if (!groupedValues[groupKey]) {
      groupedValues[groupKey] = [];
    }
    groupedValues[groupKey].push(value);
  }

  return groupedValues;
};

const AttributeValuesCheckboxes: React.FC<AttributeValuesCheckboxesProps> = ({
  selectedAttributeGroupId,
  selectedAttributes,
  setSelectedAttributes,
  attributeGroups,
  handleCheckboxChange,
  itemName,
  setItemName,
  selectedUom,
  setSelectedUom,
  handleCreateItem,
  userBackgroundColor,
}) => {
  const theme = useTheme();
  const setIsViewingAssemblyForm = useStore(useAssemblyStore).setIsViewingAssemblyForm;
  const setIsViewingItemForm = useStore(useGlobalItemStore).setIsViewingItemForm;
  const setIsCreatingAssembly = useStore(useAssemblyStore).setIsCreatingAssembly;
  const setIsEditMode = useStore(useAssemblyStore).setIsEditMode;
  const { setIsCreatingItem, setCurrentItem, isCreatingItem } = useStore(useGlobalItemStore);
  const { currentFolder } = useStore(useFolderStore);
  const [allChecked, setAllChecked] = useState(false);

  if (!selectedAttributeGroupId) return null;

  const currentGroup = attributeGroups?.find(
    group => String(group.attributegroupid) === selectedAttributeGroupId
  );

  const groupedAttributes = currentGroup?.attributevaluesList
    ? sortAndGroupAttributeValues(currentGroup.attributevaluesList)
    : {};

  const handleCancel = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setIsCreatingItem(false);
    setCurrentItem(defaultGlobalItem);
    setItemName('');
    setSelectedUom('');
    setSelectedAttributes({});
    setIsEditMode(false);
    setIsCreatingAssembly(true);
    setIsViewingAssemblyForm(true);
    setIsViewingItemForm(false);
  };

  const handleSelectAll = () => {
    if (!currentGroup?.attributevaluesList) return;

    // First, deduplicate the attributes by attributevaluename
    const uniqueValues = currentGroup.attributevaluesList.reduce(
      (acc, value) => {
        const key = value.attributevaluename;
        if (!acc[key] || acc[key].attributevalueid > value.attributevalueid) {
          // Keep the value with the lowest ID if there are duplicates
          acc[key] = value;
        }
        return acc;
      },
      {} as Record<string, (typeof currentGroup.attributevaluesList)[0]>
    );

    // Get the unique attribute value IDs
    const uniqueValueIds = Object.values(uniqueValues).map(val => val.attributevalueid.toString());

    if (allChecked) {
      setSelectedAttributes({});
      setAllChecked(false);
    } else {
      setSelectedAttributes(prevSelected => ({
        ...prevSelected,
        [selectedAttributeGroupId]: uniqueValueIds,
      }));
      setAllChecked(true);
    }
  };

  const textColor = theme.palette.text.primary;

  // Helper function to check if any attributes are selected
  const hasSelectedAttributes = Object.values(selectedAttributes).some(ids => ids.length > 0);

  // Save button disabled conditions:
  // 1. In Edit mode: Cannot save if no attributes selected, but folder check not required
  // 2. In Create mode: Cannot save if no folder selected or no attributes selected
  const isSaveDisabled =
    !itemName ||
    !selectedUom ||
    !hasSelectedAttributes ||
    (isCreatingItem && (!currentFolder || currentFolder.entitytype !== 'item'));

  return (
    <Box
      sx={{
        ml: 1,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
        overflow: 'hidden',
        color: textColor,
      }}
    >
      <FormLabel sx={{ mb: 1, color: textColor }}>Item Attributes:</FormLabel>
      <Paper
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          overflowY: 'auto',
          border: `1px solid ${theme.palette.divider}`,
          padding: 2,
          borderRadius: 2,
          mb: 2,
          backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.paper : '#fff',
          color: textColor,
        }}
      >
        {Object.entries(groupedAttributes).length ? (
          Object.entries(groupedAttributes).map(([groupKey, values]) => (
            <Box
              key={groupKey}
              sx={{
                mb: 2,
                p: 1,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
                backgroundColor: userBackgroundColor
                  ? `${userBackgroundColor}dd`
                  : theme.palette.mode === 'dark'
                    ? alpha(theme.palette.background.paper, 0.6)
                    : '#fafafa',
              }}
            >
              <Typography variant="subtitle2" sx={{ mb: 1, color: textColor }}>
                {groupKey}
              </Typography>
              <Grid container spacing={1}>
                {values.map(valueObj => {
                  if (!valueObj) return null;

                  const valueIdStr = valueObj.attributevalueid?.toString() || '';
                  const validGroupId = selectedAttributeGroupId
                    ? String(selectedAttributeGroupId)
                    : '';
                  const isChecked = (selectedAttributes[validGroupId] ?? []).includes(valueIdStr);

                  // Create tooltip content if there are variables
                  const tooltipContent =
                    valueObj.attributevariablesList?.length > 0
                      ? valueObj.attributevariablesList
                          .map(v => `${v.variable?.name}: ${v.variablevalue}`)
                          .join(', ')
                      : '';

                  return (
                    <Grid item xs={12} sm={6} md={6} lg={6} key={valueObj.attributevalueid}>
                      <Tooltip title={tooltipContent} placement="top">
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={Boolean(isChecked)}
                              onChange={() =>
                                handleCheckboxChange(selectedAttributeGroupId, valueIdStr)
                              }
                              sx={{
                                '& .MuiSvgIcon-root': {
                                  backgroundColor:
                                    theme.palette.mode === 'dark'
                                      ? 'rgba(255, 255, 255, 0.08)'
                                      : 'white',
                                  borderRadius: 1,
                                },
                                '&.Mui-checked': {
                                  color: theme.palette.primary.main,
                                },
                              }}
                            />
                          }
                          label={
                            <Typography noWrap sx={{ color: textColor }}>
                              {valueObj.attributevaluename}
                            </Typography>
                          }
                          sx={{
                            width: '100%',
                            margin: 0,
                            '& .MuiTypography-root': {
                              width: '100%',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            },
                          }}
                        />
                      </Tooltip>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          ))
        ) : (
          <Typography sx={{ color: textColor }}>No attributes available for this group.</Typography>
        )}
      </Paper>

      <Grid container spacing={2} justifyContent="center" alignItems="center">
        <Grid item xs={12} sm={4}>
          <Button onClick={handleSelectAll} variant="contained" fullWidth color="primary">
            {allChecked ? 'Deselect All' : 'Select All'}
          </Button>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Tooltip
            title={
              isCreatingItem && (!currentFolder || currentFolder.entitytype !== 'item')
                ? 'Please select an Item folder to save the item'
                : !itemName || !selectedUom
                  ? 'Item name and unit of measure are required'
                  : !hasSelectedAttributes
                    ? 'At least one attribute must be selected'
                    : ''
            }
            arrow
          >
            <span>
              <Button
                onClick={handleCreateItem}
                disabled={isSaveDisabled}
                variant="contained"
                color="success"
                fullWidth
              >
                Save
              </Button>
            </span>
          </Tooltip>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Button
            onClick={handleCancel}
            disabled={!itemName || !selectedUom}
            variant="contained"
            color="secondary"
            fullWidth
          >
            Cancel
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default React.memo(AttributeValuesCheckboxes);
